import { AnyAction } from "redux";

import {
  HOLIDAYS_ENDPOINT,
  CONTRACTS_TO_PAUSE_ENDPOINT,
} from "@config/endpoints";

import { ThunkActionDispatch } from "@typings/reduxThunkActions";

import { ASYNC_ACTION_TYPES } from "@services/FetchFacade";
import { ReduxActionNameCreator } from "@services/ReduxActionNameCreator";
import { AsyncActionResult } from "@services/AsyncActionCreatorFactory";
import { buildUrl } from "@services/BuildUrl";

import {
  getAddHolidayEndpointRequest,
  getEditHolidayEndpointRequest,
  getDeleteHolidayEndpointRequest,
} from "@app/redux/holidays/holidays.repository";
import { Holiday } from "@app/redux/holidays/holidays.typings";
import { IStore } from "@app/redux/reducers";
import { asyncActionCreator } from "@app/services/AsyncActionCreator";
import { genericPendingAction } from "@app/services/GenericPendingAction";

const action = new ReduxActionNameCreator("holidays");

export const FETCH_HOLIDAYS = action.make("fetch holidays");
export const ADD_HOLIDAY = action.make("add holiday");
export const EDIT_HOLIDAY = action.make("edit holiday");
export const DELETE_HOLIDAY = action.make("delete holiday");

export const INIT_PROCESSED_HOLIDAY = action.make("init processed hoiday");
export const UPDATE_PROCESSED_HOLIDAY = action.make("update processed holiday");
export const CLEAR_PROCESSED_HOLIDAY = action.make("clear processed holiday");

export const FETCH_CONTRACTS_TO_PAUSE = action.make("fetch contracts to pause");

export const fetchHolidays =
  () =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    dispatch(genericPendingAction(FETCH_HOLIDAYS));

    const action = await asyncActionCreator({
      url: HOLIDAYS_ENDPOINT,
      action: FETCH_HOLIDAYS,
    });

    return dispatch(action);
  };

export const addHoliday =
  () =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    const body = getAddHolidayEndpointRequest(store());

    const action = await asyncActionCreator({
      url: HOLIDAYS_ENDPOINT,
      action: ADD_HOLIDAY,
      method: ASYNC_ACTION_TYPES.POST,
      body,
    });

    return dispatch(action);
  };

export const editHoliday =
  () =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    const { body, params } = getEditHolidayEndpointRequest(store());

    const action = await asyncActionCreator({
      url: `${HOLIDAYS_ENDPOINT}/${params.id}`,
      action: EDIT_HOLIDAY,
      method: ASYNC_ACTION_TYPES.PUT,
      body,
    });

    return dispatch(action);
  };

export const deleteHoliday =
  () =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    const { id } = getDeleteHolidayEndpointRequest(store());

    const action = await asyncActionCreator({
      url: `${HOLIDAYS_ENDPOINT}/${id}`,
      action: DELETE_HOLIDAY,
      method: ASYNC_ACTION_TYPES.DELETE,
      params: { id },
    });

    return dispatch(action);
  };

export const initProcessedHoliday = (): AnyAction => ({
  type: INIT_PROCESSED_HOLIDAY,
});

export const updateProcessedHoliday = (
  payload: Partial<Holiday>,
): AnyAction => ({
  type: UPDATE_PROCESSED_HOLIDAY,
  payload,
});

export const clearProcessedHoliday = (): AnyAction => ({
  type: CLEAR_PROCESSED_HOLIDAY,
});

export const fetchContractsToPause =
  (newHolidaysStartDate: string) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    const action = await asyncActionCreator({
      action: FETCH_CONTRACTS_TO_PAUSE,
      url: buildUrl(CONTRACTS_TO_PAUSE_ENDPOINT)
        .setQuery(
          "newHolidaysStartDate",
          encodeURIComponent(newHolidaysStartDate),
        )
        .done(),
    });

    return dispatch(action);
  };
